import { useState } from 'react';

import { Badge, Skeleton } from './ui.jsx';
import { api } from '../lib/api.js';
import { currency, cx, rate, tenure } from '../lib/format.js';
import { useAsync, useDebounced } from '../lib/useAsync.js';

/**
 * EmiCalculator — an interactive quote for an arbitrary amount.
 *
 * Calls GET /api/emi/quote rather than reimplementing the formula in the browser.
 * That is the point: there is exactly one EMI engine, it lives on the server, and
 * this widget cannot drift away from the plans stored against real products.
 */

const FUND_TYPES = [
  { value: 'equity', label: 'Equity', hint: '50% LTV · ~12% assumed growth' },
  { value: 'hybrid', label: 'Hybrid', hint: '65% LTV · ~10% assumed growth' },
  { value: 'debt', label: 'Debt', hint: '80% LTV · ~7% assumed growth' },
];

export default function EmiCalculator() {
  const [amount, setAmount] = useState(75000);
  const [fundType, setFundType] = useState('equity');

  const debouncedAmount = useDebounced(amount, 250);

  const { data, loading, error } = useAsync(
    (signal) => api.getEmiQuote(debouncedAmount, fundType, signal),
    [debouncedAmount, fundType],
  );

  const plans = data?.plans ?? [];
  const collateral = plans[0]?.collateral;

  return (
    <section className="border-t border-ink-200/70 bg-white py-16">
      <div className="container-page">
        <div className="grid gap-10 lg:grid-cols-[22rem_1fr] lg:gap-14">
          {/* ── Controls ──────────────────────────────────────────────────── */}
          <div>
            <Badge tone="brand">Calculator</Badge>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-ink-950">
              Work out an EMI for any amount
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-600">
              Drag the slider to see how the instalment, total interest and required pledge change across every
              tenure we offer.
            </p>

            <div className="mt-8">
              <div className="flex items-baseline justify-between">
                <label htmlFor="emi-amount" className="text-sm font-semibold text-ink-800">
                  Purchase amount
                </label>
                <output htmlFor="emi-amount" className="text-xl font-bold text-brand-700">
                  {currency(amount)}
                </output>
              </div>

              <input
                id="emi-amount"
                type="range"
                min={5000}
                max={200000}
                step={1000}
                value={amount}
                onChange={(event) => setAmount(Number(event.target.value))}
                className="mt-3 h-2 w-full cursor-pointer appearance-none rounded-full bg-ink-200 accent-brand-600"
                aria-describedby="emi-amount-range"
              />
              <p id="emi-amount-range" className="mt-1.5 flex justify-between text-xs text-ink-500">
                <span>{currency(5000)}</span>
                <span>{currency(200000)}</span>
              </p>
            </div>

            <fieldset className="mt-8">
              <legend className="text-sm font-semibold text-ink-800">Collateral fund type</legend>
              <p className="mt-1 text-xs text-ink-500">
                Less volatile funds are advanced at a higher loan-to-value, so you pledge less.
              </p>

              <div className="mt-3 space-y-2">
                {FUND_TYPES.map((option) => (
                  <label
                    key={option.value}
                    className={cx(
                      'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                      fundType === option.value
                        ? 'border-brand-400 bg-brand-50/60'
                        : 'border-ink-200 hover:bg-ink-50',
                    )}
                  >
                    <input
                      type="radio"
                      name="fundType"
                      value={option.value}
                      checked={fundType === option.value}
                      onChange={() => setFundType(option.value)}
                      className="mt-0.5 accent-brand-600"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-ink-900">{option.label}</span>
                      <span className="block text-xs text-ink-500">{option.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {collateral && !loading && (
              <div className="mt-8 rounded-xl bg-growth-50 p-4 ring-1 ring-inset ring-growth-200">
                <p className="text-xs font-semibold uppercase tracking-wide text-growth-800">Units to pledge</p>
                <p className="mt-1 text-2xl font-bold text-growth-900">{currency(collateral.pledgeRequired)}</p>
                <p className="mt-1 text-xs leading-relaxed text-growth-800">
                  At {collateral.ltv}% LTV. Nothing is sold — these units stay invested and remain yours.
                </p>
              </div>
            )}
          </div>

          {/* ── Results ───────────────────────────────────────────────────── */}
          <div>
            {error ? (
              <p className="surface p-6 text-sm text-red-700" role="alert">
                {error.message}
              </p>
            ) : (
              <div className="overflow-hidden rounded-card border border-ink-200/70">
                <table className="w-full text-sm">
                  <caption className="sr-only">EMI options for {currency(amount)}</caption>
                  <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                    <tr>
                      <th scope="col" className="px-4 py-3 font-semibold">Tenure</th>
                      <th scope="col" className="px-4 py-3 font-semibold">Rate</th>
                      <th scope="col" className="px-4 py-3 text-right font-semibold">Monthly</th>
                      <th scope="col" className="hidden px-4 py-3 text-right font-semibold sm:table-cell">Interest</th>
                      <th scope="col" className="px-4 py-3 text-right font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200/70 bg-white">
                    {loading
                      ? Array.from({ length: 6 }, (_, i) => (
                          <tr key={i}>
                            {Array.from({ length: 5 }, (_, j) => (
                              <td key={j} className={cx('px-4 py-3.5', j === 3 && 'hidden sm:table-cell')}>
                                <Skeleton className="h-4 w-full" />
                              </td>
                            ))}
                          </tr>
                        ))
                      : plans.map((plan) => (
                          <tr key={plan.tenureMonths} className="transition-colors hover:bg-brand-50/40">
                            <th scope="row" className="px-4 py-3.5 text-left font-semibold text-ink-900">
                              {tenure(plan.tenureMonths)}
                            </th>
                            <td className="px-4 py-3.5">
                              {plan.isNoCostEmi ? (
                                <Badge tone="growth">No cost</Badge>
                              ) : (
                                <span className="text-ink-700">{rate(plan.interestRate)} p.a.</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-right font-bold text-ink-950">
                              {currency(plan.monthlyAmount)}
                            </td>
                            <td className="hidden px-4 py-3.5 text-right text-ink-600 sm:table-cell">
                              {plan.totalInterest === 0 ? '—' : currency(plan.totalInterest)}
                            </td>
                            <td className="px-4 py-3.5 text-right text-ink-700">{currency(plan.totalPayable)}</td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-3 text-xs leading-relaxed text-ink-500">
              Instalments are calculated on a reducing-balance basis. A no-cost plan splits the amount evenly with
              no interest charged.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
